import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Wallet, TrendingDown, Banknote, Hash, Calendar, Tag, BarChart3, Clock, ChevronRight, Building2,
  AlertCircle,
} from 'lucide-react';
import { expenseAPI } from '../../services/api';
import { useToast } from '../UIProvider';
import {
  DatePresetPills, getDatePreset, formatCurrency, formatDate, TypeBadge, PaymentBadge, LoadingRow, errorText,
} from './shared';

// The figures at a glance for the chosen department: cash in hand (a position, all time), what
// the period spent and drew, where it went, the last six months, which departments have gone
// quiet, and the newest entries.

const Bars = ({ rows, nameOf, max }) => (
  <div className="space-y-3">
    {rows.map((r, i) => {
      const amount = Number(r.total || 0);
      const pct = max > 0 ? (amount / max) * 100 : 0;
      return (
        <div key={i}>
          <div className="flex items-center justify-between text-sm mb-1 gap-3">
            <span className="text-gray-700 font-medium truncate">{nameOf(r)}</span>
            <span className="text-gray-500 font-medium flex-shrink-0">{formatCurrency(amount)}<span className="text-gray-300"> · {r.count}</span></span>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-2">
            <div className="bg-emerald-500 h-2 rounded-full transition-all duration-500" style={{ width: `${Math.max(pct, 2)}%` }} />
          </div>
        </div>
      );
    })}
  </div>
);

const ExpenseDashboard = ({ department, cats, refreshKey, onGoToEntries }) => {
  const showToast = useToast();
  const [preset, setPreset] = useState('thisMonth');
  const [range, setRange] = useState(() => getDatePreset('thisMonth'));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await expenseAPI.getDashboard(range.from, range.to, department !== 'ALL' ? department : undefined);
      setData(d);
    } catch (err) {
      showToast('error', errorText(err, 'Failed to load the dashboard'));
    } finally {
      setLoading(false);
    }
  }, [range, department, showToast]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const stats = data?.stats || {};
  const cash = data?.cash || {};
  const byDepartment = data?.spendByDepartment || [];
  const byCategory = data?.spendByCategory || [];
  const monthly = data?.monthlyTrend || [];
  const lastBy = data?.lastByDepartment || [];
  const recent = data?.recentEntries || [];
  const scopedName = department !== 'ALL' ? (cats.byId[department]?.name || 'department') : 'all departments';

  const { maxDept, maxCat, maxMonth } = useMemo(() => ({
    maxDept: Math.max(...(data?.spendByDepartment || []).map(r => Number(r.total || 0)), 0),
    maxCat: Math.max(...(data?.spendByCategory || []).map(r => Number(r.total || 0)), 0),
    maxMonth: Math.max(...(data?.monthlyTrend || []).map(m => Math.max(Number(m.spent || 0), Number(m.withdrawn || 0))), 0),
  }), [data]);

  const cards = [
    { icon: Wallet, label: 'Cash in hand', value: formatCurrency(cash.cashInHand), sub: `${scopedName}, all time`, tone: Number(cash.cashInHand) < 0 ? 'text-red-600' : 'text-gray-900', gradient: 'from-emerald-500 to-teal-600' },
    { icon: TrendingDown, label: 'Spent', value: formatCurrency(stats.totalExpenses), sub: `${stats.expenseCount || 0} expenses in the period`, gradient: 'from-red-500 to-rose-600' },
    { icon: Banknote, label: 'Withdrawn', value: formatCurrency(stats.totalWithdrawals), sub: `${stats.withdrawalCount || 0} withdrawals in the period`, gradient: 'from-blue-500 to-indigo-600' },
    { icon: Hash, label: 'Paid in cash', value: formatCurrency(stats.cashExpenses), sub: 'Out of the cash box', gradient: 'from-amber-500 to-orange-600' },
  ];

  if (loading && !data) {
    return <div className="bg-white rounded-2xl shadow-xl"><LoadingRow text="Loading the dashboard..." /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl shadow-xl p-5">
        <div className="flex items-center gap-3 flex-wrap">
          <Calendar className="h-5 w-5 text-gray-400" />
          <DatePresetPills value={preset} onChange={(p) => { setPreset(p); setRange(getDatePreset(p)); }} />
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {cards.map((s, i) => (
          <div key={i} className="bg-white rounded-xl shadow-md p-4 hover:shadow-lg transition-all">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="text-gray-500 text-sm truncate" title={s.label}>{s.label}</p>
                <p className={`text-2xl font-bold mt-1 ${s.tone || 'text-gray-900'}`}>{s.value}</p>
                <p className="text-[11px] text-gray-400 mt-0.5">{s.sub}</p>
              </div>
              <div className={`w-10 h-10 bg-gradient-to-br ${s.gradient} rounded-xl flex items-center justify-center flex-shrink-0`}>
                <s.icon className="h-5 w-5 text-white" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {department === 'ALL' && (cash.byDepartment || []).length > 0 && (
        <div className="bg-white rounded-2xl shadow-xl">
          <div className="p-5 border-b border-gray-100 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Building2 className="h-5 w-5 text-emerald-600" /> Cash by department
            </h3>
            <span className="text-xs text-gray-400">All time: every withdrawal less every expense</span>
          </div>
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Department</th>
                <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Withdrawn</th>
                <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Spent</th>
                <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Cash in hand</th>
                <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Entries</th>
              </tr>
            </thead>
            <tbody>
              {cash.byDepartment.map((r, i) => (
                <tr key={r.rootId || i} className="border-b border-gray-50">
                  <td className="px-5 py-3 text-sm font-medium text-gray-900">{r.name}</td>
                  <td className="px-5 py-3 text-sm text-blue-700 text-right">{formatCurrency(r.withdrawn)}</td>
                  <td className="px-5 py-3 text-sm text-red-700 text-right">{formatCurrency(r.spent)}</td>
                  <td className={`px-5 py-3 text-sm font-semibold text-right ${Number(r.cashInHand) < 0 ? 'text-red-600' : 'text-emerald-700'}`}>
                    {formatCurrency(r.cashInHand)}
                    {Number(r.cashInHand) < 0 && <AlertCircle className="inline h-4 w-4 ml-1 text-red-500" aria-label="Below zero" />}
                  </td>
                  <td className="px-5 py-3 text-sm text-gray-500 text-right">{r.withdrawalCount} in · {r.expenseCount} out</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl shadow-xl p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Tag className="h-5 w-5 text-emerald-600" />
            {department === 'ALL' ? 'Spending by department' : 'Spending by category'}
          </h3>
          {(department === 'ALL' ? byDepartment : byCategory).length === 0 ? (
            <p className="text-gray-400 text-sm text-center py-8">Nothing spent in this period</p>
          ) : department === 'ALL' ? (
            <Bars rows={byDepartment} nameOf={r => r.name} max={maxDept} />
          ) : (
            <Bars rows={byCategory} nameOf={r => r.name} max={maxCat} />
          )}
          {department === 'ALL' && byCategory.length > 0 && (
            <div className="mt-6">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Top categories</p>
              <ul className="divide-y divide-gray-100">
                {byCategory.map((c, i) => (
                  <li key={i} className="flex items-center justify-between py-1.5 text-sm">
                    <span className="text-gray-700 truncate">{c.department ? `${c.department} › ` : ''}{c.name}</span>
                    <span className="text-gray-500 font-medium flex-shrink-0">{formatCurrency(c.total)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-emerald-600" /> Last six months
          </h3>
          {monthly.length === 0 ? (
            <p className="text-gray-400 text-sm text-center py-8">Nothing in the last six months</p>
          ) : (
            <div className="space-y-3">
              {monthly.map((m) => {
                const spent = Number(m.spent || 0);
                const withdrawn = Number(m.withdrawn || 0);
                return (
                  <div key={m.month}>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-gray-600 font-medium">{m.label}</span>
                      <span className="text-gray-500">
                        <span className="text-red-600">{formatCurrency(spent)} spent</span>
                        <span className="text-gray-300"> · </span>
                        <span className="text-blue-600">{formatCurrency(withdrawn)} drawn</span>
                      </span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2 mb-1">
                      <div className="bg-red-400 h-2 rounded-full transition-all" style={{ width: `${maxMonth > 0 ? Math.max((spent / maxMonth) * 100, 1) : 0}%` }} />
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-1.5">
                      <div className="bg-blue-400 h-1.5 rounded-full transition-all" style={{ width: `${maxMonth > 0 ? Math.max((withdrawn / maxMonth) * 100, 1) : 0}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {lastBy.length > 0 && (
        <div className="bg-white rounded-2xl shadow-xl p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-1 flex items-center gap-2">
            <Clock className="h-5 w-5 text-emerald-600" /> Last expense per department
          </h3>
          <p className="text-xs text-gray-400 mb-4">A department that has gone quiet is usually one whose bills have not been entered.</p>
          <ul className="divide-y divide-gray-100">
            {lastBy.map((r, i) => {
              const stale = r.daysAgo === null || r.daysAgo === undefined || r.daysAgo > 14;
              return (
                <li key={r.rootId || i} className="flex items-center justify-between gap-4 py-2.5">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900">{r.name}</p>
                    <p className="text-xs text-gray-500 truncate">
                      {r.lastDate ? `${r.description}, ${formatCurrency(r.amount)}` : 'Nothing entered yet'}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className={`text-sm font-semibold ${stale ? 'text-amber-600' : 'text-gray-700'}`}>
                      {r.lastDate ? formatDate(r.lastDate) : '—'}
                    </p>
                    {r.lastDate && <p className="text-xs text-gray-400">{r.daysAgo === 0 ? 'today' : `${r.daysAgo} days ago`}</p>}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Clock className="h-5 w-5 text-emerald-600" /> Recent entries
          </h3>
          <button onClick={onGoToEntries} className="text-sm text-emerald-600 hover:text-emerald-700 font-medium flex items-center gap-1">
            View all <ChevronRight className="h-4 w-4" />
          </button>
        </div>
        {recent.length === 0 ? (
          <p className="text-gray-400 text-sm text-center py-8">No entries yet</p>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Description</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Department</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Paid by</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Amount</th>
              </tr>
            </thead>
            <tbody>
              {recent.map((entry) => (
                <tr key={entry.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{formatDate(entry.entryDate)}</td>
                  <td className="px-4 py-3 text-sm text-gray-900 font-medium">
                    {entry.description}
                    <div className="mt-0.5"><TypeBadge type={entry.entryType} /></div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{entry.categoryPath || '-'}</td>
                  <td className="px-4 py-3"><PaymentBadge mode={entry.paymentMode} /></td>
                  <td className={`px-4 py-3 text-sm font-semibold text-right ${entry.entryType === 'WITHDRAWAL' ? 'text-blue-700' : 'text-red-700'}`}>
                    {formatCurrency(entry.amount)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default ExpenseDashboard;
